import {
  type Analytics,
  type AnalyticsConfig,
  analyticsFromPostHog,
} from '@zealth/core/analytics'
import posthog from 'posthog-js'

// Builds the PostHog Analytics adapter, or returns null when apiKey
// is missing (local dev / test) or posthog.init throws. The caller
// (`initAnalytics`) early-returns on null.
//
// This file is one of two known direct importers of `posthog-js` —
// it owns init. The other is `index.tsx`, which imports `posthog`
// only to pass the already-initialized singleton to
// <PostHogProvider client={posthog}> for usePostHog() hook access.
// PostHog's React docs warn against random call-sites importing the
// singleton because their apiKey-prop init pattern defers init into
// a React effect; we don't use that pattern — init happens here,
// synchronously, at module load — so these two imports are safe.
export function createPostHogAnalytics(
  config: AnalyticsConfig
): Analytics | null {
  const { apiKey, apiHost, deployMode = 'release', client } = config

  if (!apiKey) return null

  try {
    posthog.init(apiKey, {
      api_host: apiHost ?? 'https://us.i.posthog.com',
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      respect_dnt: true,
      advanced_disable_feature_flags: true,
      // posthog-js prefers these over the project's masking setting, so
      // switching that setting back to "Normal" cannot expose customers' plans.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
        blockSelector: 'img, svg, canvas, video',
      },
    })
    posthog.register({ client, deploy_mode: deployMode })
  } catch (err) {
    console.error('[analytics] posthog.init failed; skipping registration', err)
    return null
  }

  return analyticsFromPostHog(posthog)
}
